import { cx } from './primitives.jsx'

/**
 * The group's mark: an atom with three orbits around a globe.
 *
 * Inlined rather than referenced as `<img src="logo.svg">` on purpose. The
 * strokes resolve to `currentColor`, so the mark follows the surrounding text
 * colour and therefore the theme — an external SVG has no inherited colour to
 * read and would need one file per palette.
 *
 * Only the seven skeleton shapes are drawn here. The source artwork also
 * carries 72 paths for the continents inside the globe; at the sizes a mark is
 * actually used (24–40 px) that detail sits inside an 18-unit radius and turns
 * to mud, while costing 32 KB. The full-detail file lives at public/logo.svg
 * for large or printed use.
 *
 * Layer rule holds: no colour, no size, no stroke width appears below. The
 * geometry is the asset; everything about how it looks is in the theme.
 *
 * 课题组标识：三条轨道环绕地球的原子。
 *
 * 刻意内联，而不是用 `<img src="logo.svg">` 引用。描边解析为 `currentColor`，
 * 因此标识跟随周围文字颜色、也就跟随主题——外链 SVG 没有可继承的颜色可读，
 * 那样就得为每套配色各存一个文件。
 *
 * 这里只画七个骨架形状。原始素材里还有 72 条描绘地球大陆的 path；在标识真正会被
 * 使用的尺寸下（24–40 像素），这些细节挤在半径 18 个单位的圆里只会糊成一团，
 * 却要付出 32KB。完整细节版放在 public/logo.svg，供大尺寸或印刷使用。
 *
 * 分层规则依然成立：以下不出现任何颜色、尺寸或描边宽度。几何形状是素材本身，
 * 「长什么样」全部交给主题。
 *
 * @param {object} props Props / 属性。
 * @param {'sm'|'md'|'lg'} [props.size] Size step / 尺寸档位。
 * @param {string} [props.title] Accessible name; omit for decorative use / 可访问名称，装饰性使用时省略。
 * @returns {JSX.Element} The mark / 标识。
 */
export function Logo({ size = 'md', title, className }) {
  return (
    <svg
      className={cx('ui-logo', `ui-logo--${size}`, className)}
      viewBox="0 0 282.07 253.72"
      // A mark next to the group's own name is decoration, not information —
      // announcing it would just make a screen reader say the name twice.
      // 标识与组名并排时属于装饰而非信息——播报它只会让读屏软件把名字念两遍。
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : 'true'}
      focusable="false"
    >
      <g className="ui-logo__orbits">
        <ellipse
          cx="252.24"
          cy="380.24"
          rx="139.53"
          ry="53.5"
          transform="translate(-314.38 153.62) rotate(-60)"
        />
        <ellipse cx="141.03" cy="125.29" rx="139.53" ry="53.5" />
        <ellipse
          cx="252.24"
          cy="380.24"
          rx="53.5"
          ry="139.53"
          transform="translate(-267.53 -77.88) rotate(-30)"
        />
      </g>
      <circle className="ui-logo__nucleus" cx="141.03" cy="125.29" r="18.43" />
      <g className="ui-logo__electrons">
        <circle cx="60.35" cy="17.47" r="9.21" />
        <circle cx="268.67" cy="143.72" r="9.21" />
        <circle cx="103.72" cy="243.01" r="9.21" />
      </g>
    </svg>
  )
}
